(() => {
  const SECTION_SELECTOR = '[data-section-type="collection-template"]';
  const GRID_SELECTOR = '[data-product-container]';
  const ITEM_SELECTOR = ':scope > .m-product-item';
  const AVAILABILITY_PARAM = 'filter.v.availability';
  const responseCache = new Map();

  class PixQualityCollectionStockOrder {
    constructor(section) {
      this.section = section;
      this.grid = section.querySelector(GRID_SELECTOR);
      this.sectionId = section.dataset.sectionId;
      this.paginationType = section.dataset.paginationType || 'paginate';
      this.pageSize = Math.max(1, Number(section.dataset.pqPageSize) || 12);
      this.availableCount = Math.max(0, Number(section.dataset.pqAvailableCount) || 0);
      this.unavailableCount = Math.max(0, Number(section.dataset.pqUnavailableCount) || 0);
      this.totalCount = this.availableCount + this.unavailableCount;
      this.requestToken = 0;
      this.gridObserver = null;
      this.section.dataset.pqStockOrderReady = 'true';

      if (!this.grid || !this.shouldGroupByAvailability()) return;

      this.gridObserver = new MutationObserver(this.onGridMutation);
      this.gridObserver.observe(this.grid, { childList: true });

      const currentItems = this.grid.querySelectorAll(ITEM_SELECTOR).length;
      if (this.paginationType === 'paginate') {
        const page = Math.max(1, Number(new URL(window.location.href).searchParams.get('page')) || 1);
        const start = (page - 1) * this.pageSize;
        this.compose(start, Math.min(this.pageSize, Math.max(0, this.totalCount - start)));
      } else {
        this.compose(0, Math.min(currentItems, this.totalCount));
      }
    }

    destroy() {
      this.requestToken += 1;
      this.gridObserver?.disconnect();
    }

    shouldGroupByAvailability() {
      if (this.totalCount <= 1) return false;
      const selectedValues = [...new Set(new URL(window.location.href).searchParams.getAll(AVAILABILITY_PARAM))];
      return selectedValues.length !== 1;
    }

    onGridMutation = (mutations) => {
      if (this.paginationType === 'paginate') return;

      const hasNativeItems = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            node.matches('.m-product-item') &&
            node.dataset.pqStockComposed !== 'true'
        )
      );
      if (!hasNativeItems) return;

      const requestedLength = Math.min(this.grid.querySelectorAll(ITEM_SELECTOR).length, this.totalCount);
      this.compose(0, requestedLength);
    };

    async compose(start, length) {
      if (length <= 0) return;

      const token = ++this.requestToken;
      this.setLoading(true);

      try {
        const end = Math.min(start + length, this.totalCount);
        const availableStart = Math.min(start, this.availableCount);
        const availableEnd = Math.min(end, this.availableCount);
        const unavailableStart = Math.max(0, start - this.availableCount);
        const unavailableEnd = Math.max(0, end - this.availableCount);

        const [availableItems, unavailableItems] = await Promise.all([
          this.fetchSlice('1', availableStart, Math.max(0, availableEnd - availableStart)),
          this.fetchSlice('0', unavailableStart, Math.max(0, unavailableEnd - unavailableStart)),
        ]);

        if (token !== this.requestToken || !this.section.isConnected) return;

        const items = [...availableItems, ...unavailableItems];
        if (items.length !== end - start) {
          throw new Error(`Expected ${end - start} product cards, received ${items.length}.`);
        }

        items.forEach((item) => {
          item.dataset.pqStockComposed = 'true';
        });
        this.grid.replaceChildren(...items);
        this.refreshProductFeatures();
      } catch (error) {
        if (token === this.requestToken) {
          console.warn('PixQuality stock-first ordering kept the native product order.', error);
        }
      } finally {
        if (token === this.requestToken) this.setLoading(false);
      }
    }

    async fetchSlice(availability, offset, length) {
      if (length <= 0) return [];

      const items = [];
      let remaining = length;
      let currentOffset = offset;

      while (remaining > 0) {
        const page = Math.floor(currentOffset / this.pageSize) + 1;
        const offsetOnPage = currentOffset % this.pageSize;
        const pageItems = await this.fetchGroupPage(availability, page);
        const selectedItems = pageItems.slice(offsetOnPage, offsetOnPage + remaining);
        items.push(...selectedItems);

        if (selectedItems.length === 0) break;
        remaining -= selectedItems.length;
        currentOffset += selectedItems.length;
      }

      return items;
    }

    async fetchGroupPage(availability, page) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('page');
      url.searchParams.delete(AVAILABILITY_PARAM);
      url.searchParams.append(AVAILABILITY_PARAM, availability);
      if (page > 1) url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.sectionId);

      const cacheKey = url.toString();
      if (!responseCache.has(cacheKey)) {
        responseCache.set(
          cacheKey,
          fetch(url, { credentials: 'same-origin' })
            .then((response) => {
              if (!response.ok) throw new Error(`Collection section request failed with ${response.status}.`);
              return response.text();
            })
            .catch((error) => {
              responseCache.delete(cacheKey);
              throw error;
            })
        );
      }

      const html = await responseCache.get(cacheKey);
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const resultGrid = documentFragment.querySelector(`${SECTION_SELECTOR} ${GRID_SELECTOR}`);
      if (!resultGrid) throw new Error('Collection section response did not include a product grid.');
      return Array.from(resultGrid.querySelectorAll(ITEM_SELECTOR));
    }

    setLoading(loading) {
      this.grid.setAttribute('aria-busy', String(loading));
      this.grid.style.opacity = loading ? '0.55' : '';
    }

    refreshProductFeatures() {
      window.MinimogTheme?.Wishlist?.setWishlistButtonsState();
      window.MinimogTheme?.CompareProduct?.setCompareButtonsState();
      window.MinimogEvents?.emit?.('ON_PRODUCT_LIST_UPDATED');
    }
  }

  let activeInstance = null;

  const initialize = () => {
    const section = document.querySelector(`${SECTION_SELECTOR}:not([data-pq-stock-order-ready="true"])`);
    if (!section) return;

    activeInstance?.destroy();
    activeInstance = new PixQualityCollectionStockOrder(section);
  };

  new MutationObserver(initialize).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  initialize();
})();
